import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Heart,
  CreditCard,
  Phone,
  MessageSquare,
  FileCheck,
  Search,
  PenTool,
  Wrench,
  BarChart3,
  ScrollText,
  Key,
  Sparkles,
  CheckCircle,
  Star,
} from "lucide-react";
import SEO from "@/components/SEO";
import { buildGraph, webPageJsonLd, breadcrumbJsonLd, faqJsonLd } from "@/data/seo";

const RED = "#D41F30";
const TEAM_PHONE = "571-520-8688";
const TEAM_PHONE_TEL = "tel:+15715208688";
const TEAM_PHONE_SMS = "sms:+15715208688";

// ─────────────────────────────────────────────────────────────────────────────
// Section blueprint
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = [
  {
    step: 1,
    Icon: Heart,
    eyebrow: "Start with your why",
    title: "Know why you want to buy a home",
    intro: "Buying a home is a big decision. Ask yourself:",
    bullets: [
      "Are you tired of renting?",
      "Do you want to build equity?",
      "Are you looking for long-term stability?",
    ],
    callout: {
      type: "star",
      text: "Knowing your “why” helps guide your plan.",
    },
    image:
      "/img/7gn3bl89.webp",
    imageAlt: "Couple discussing their homebuying plans",
  },
  {
    step: 2,
    Icon: CreditCard,
    eyebrow: "Get your numbers straight",
    title: "Check your credit & finances",
    bullets: [
      "Know your credit score",
      "Review your monthly income vs. debt",
      "Start saving for a down payment (even 3% may be enough)",
    ],
    callout: {
      type: "pro",
      text: "Pro tip: Don’t open new credit cards or make big purchases during this time.",
    },
    image:
      "/img/xfirm7x8.webp",
    imageAlt: "Reviewing finances and credit",
  },
  {
    step: 3,
    Icon: Phone,
    eyebrow: "Step three",
    title: "Talk to a homebuying team",
    intro:
      "Get connected with a CityWorth agent and mortgage expert. They’ll help you:",
    bullets: [
      "Understand how much home you can afford",
      "Get pre-approved for a loan",
      "Explore programs that reduce your upfront costs",
    ],
    cta: "callTeam",
    image:
      "/img/rwit2fhc.webp",
    imageAlt: "The CityWorth team",
  },
  {
    step: 4,
    Icon: FileCheck,
    eyebrow: "Make it official",
    title: "Get pre-approved",
    intro: "A loan pre-approval tells sellers you’re serious. It also:",
    bullets: [
      "Helps you understand your true price range",
      "Shows what your monthly payment will be",
      "Reveals what loan programs you may qualify for",
    ],
    programs: [
      { name: "FHA", desc: "Great for first-timers with lower credit scores and smaller down payments" },
      { name: "Conventional", desc: "Ideal if you have a strong credit profile" },
      { name: "VA", desc: "Zero down for eligible veterans or active-duty service members" },
      { name: "USDA", desc: "No down payment for homes in qualifying rural areas" },
    ],
    cta: "startJourney",
    image:
      "/img/asxh00wx.webp",
    imageAlt: "Signing a loan pre-approval letter",
  },
  {
    step: 5,
    Icon: Search,
    eyebrow: "The fun part",
    title: "Start your home search",
    intro: "Think about what matters most:",
    bullets: ["Location", "Size", "School district", "Commute"],
    callout: {
      type: "home",
      text: "Your agent will help you find homes that match your goals.",
    },
    image:
      "/img/6ti2cvfw.webp",
    imageAlt: "Touring homes with an agent",
  },
  {
    step: 6,
    Icon: PenTool,
    eyebrow: "Make your move",
    title: "Make an offer",
    intro: "Once you find the right home:",
    bullets: [
      "Your agent will help you submit an offer",
      "Your agent will help you negotiate with the seller",
      "Once accepted, you’ll submit your Earnest Money Deposit (typically 1% of sales price), which shows you’re committed",
    ],
    callout: {
      type: "hot",
      text: "Hot tip: Your CityWorth agent is trained to negotiate with sellers to help cover your closing costs. Many buyers don’t realize it’s negotiable, it can save you thousands upfront.",
    },
    image:
      "/img/yo93e753.webp",
    imageAlt: "Signing a home offer",
  },
  {
    step: 7,
    Icon: Wrench,
    eyebrow: "Protect your investment",
    title: "Home inspection",
    sections: [
      {
        heading: "Reveals hidden issues",
        body:
          "A licensed inspector checks the roof, plumbing, electric, HVAC, foundation, and more to uncover problems before you buy.",
      },
      {
        heading: "Helps you negotiate",
        body:
          "If repairs are needed, you can ask the seller to fix them, offer a credit, or lower the price.",
      },
    ],
    image:
      "/img/vfnzkh86.webp",
    imageAlt: "Home inspector at work",
  },
  {
    step: 8,
    Icon: BarChart3,
    eyebrow: "Confirm the value",
    title: "Appraisal",
    sections: [
      {
        heading: "Appraisal protects you from overpaying",
        body: "If the value comes in low, you can renegotiate or walk away.",
      },
      {
        heading: "Based on comparable sales",
        body:
          "An appraiser compares the home to similar recent sales in the area to determine fair market value.",
      },
    ],
    callout: {
      type: "tip",
      text: "If the appraisal comes in low, don’t panic, you can renegotiate or challenge it with your agent’s help.",
    },
    image:
      "/img/be6rvll8.webp",
    imageAlt: "Reviewing an appraisal report",
  },
  {
    step: 9,
    Icon: ScrollText,
    eyebrow: "Almost there",
    title: "Finalize your loan",
    intro: "Submit all final documents to your lender and get ready to:",
    bullets: [
      "Lock in your interest rate",
      "Sign closing disclosures",
      "Wire funds or bring a cashier’s check to closing",
    ],
    callout: {
      type: "tip",
      text: "Respond fast to your lender, delays in paperwork can delay closing.",
    },
    cta: "callTeam",
    image:
      "/img/v9ikrw55.webp",
    imageAlt: "Final loan paperwork",
  },
  {
    step: 10,
    Icon: Key,
    eyebrow: "The big day",
    title: "Closing day",
    intro: "Sign the paperwork, get the keys, and celebrate, you’re officially a homeowner!",
    callout: {
      type: "bonus",
      text: "Bonus: If you close on your home in July, your first mortgage payment likely won’t be due until September, it skips a month and gives you a little breathing room!",
    },
    image:
      "/img/qqov8isa.webp",
    imageAlt: "Happy new homeowners with keys",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Inline elements
// ─────────────────────────────────────────────────────────────────────────────

const Callout = ({ type, text }) => {
  const styles = {
    pro: { bg: "bg-green-50", border: "border-green-200", icon: <CheckCircle className="w-5 h-5 text-green-600" /> },
    star: { bg: "bg-amber-50", border: "border-amber-200", icon: <Star className="w-5 h-5 text-amber-500 fill-amber-400" /> },
    hot: { bg: "bg-orange-50", border: "border-orange-200", icon: <Sparkles className="w-5 h-5 text-orange-600" /> },
    tip: { bg: "bg-blue-50", border: "border-blue-200", icon: <Sparkles className="w-5 h-5 text-blue-600" /> },
    home: { bg: "bg-red-50", border: "border-red-100", icon: <Heart className="w-5 h-5 text-[#D41F30] fill-[#D41F30]" /> },
    bonus: { bg: "bg-purple-50", border: "border-purple-200", icon: <Sparkles className="w-5 h-5 text-purple-600" /> },
  };
  const s = styles[type] || styles.tip;
  return (
    <div className={`mt-6 rounded-2xl border ${s.border} ${s.bg} px-5 py-4 flex items-start gap-3`}>
      <div className="flex-shrink-0 mt-0.5">{s.icon}</div>
      <p className="text-sm md:text-base text-gray-800 leading-relaxed">{text}</p>
    </div>
  );
};

const CallTeamCTA = () => (
  <div className="mt-8 flex flex-col sm:flex-row gap-4">
    <a
      href={TEAM_PHONE_TEL}
      className="inline-flex items-center justify-center bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-5 text-base md:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
      data-testid="guide-call-team"
    >
      <Phone className="mr-2 w-5 h-5" />
      Talk to your team
    </a>
    <a
      href={TEAM_PHONE_SMS}
      className="inline-flex items-center justify-center border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full px-8 py-5 text-base md:text-lg font-semibold transition-all duration-300"
      data-testid="guide-text-team"
    >
      <MessageSquare className="mr-2 w-5 h-5" />
      Text your team
    </a>
  </div>
);

const StartJourneyCTA = () => (
  <div className="mt-8">
    <Button
      className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-7 text-base md:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
      onClick={() => window.location.assign("/get-started")}
      data-testid="guide-start-journey"
    >
      Start my homebuying plan
      <ArrowRight className="ml-2 w-5 h-5" />
    </Button>
  </div>
);

const SectionRow = ({ section, index }) => {
  const reverse = index % 2 === 1;
  const { Icon } = section;

  return (
    <section
      className={`py-16 md:py-24 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
      data-testid={`guide-section-${section.step}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Text column */}
          <div className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
                <Icon className="w-6 h-6" style={{ color: RED }} />
              </div>
              <div>
                <div className="text-xs font-bold tracking-[0.18em] uppercase" style={{ color: RED }}>
                  Step {section.step}
                </div>
                <div className="text-sm text-gray-500">{section.eyebrow}</div>
              </div>
            </div>

            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-[1.05] tracking-tight mb-5">
              {section.title}
            </h2>

            {section.intro && (
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-5">{section.intro}</p>
            )}

            {section.bullets && (
              <ul className="space-y-2.5">
                {section.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                    <span className="text-base md:text-lg text-gray-700">{b}</span>
                  </li>
                ))}
              </ul>
            )}

            {section.sections && (
              <div className="space-y-5 mt-2">
                {section.sections.map((s, i) => (
                  <div key={i}>
                    <h3 className="font-display text-xl font-bold text-gray-900 mb-1">{s.heading}</h3>
                    <p className="text-base text-gray-600 leading-relaxed">{s.body}</p>
                  </div>
                ))}
              </div>
            )}

            {section.programs && (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {section.programs.map((p) => (
                  <div key={p.name} className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="text-sm font-black tracking-wide" style={{ color: RED }}>{p.name}</div>
                    <div className="text-sm text-gray-600 mt-1 leading-relaxed">{p.desc}</div>
                  </div>
                ))}
              </div>
            )}

            {section.callout && <Callout type={section.callout.type} text={section.callout.text} />}

            {section.cta === "callTeam" && <CallTeamCTA />}
            {section.cta === "startJourney" && <StartJourneyCTA />}
          </div>

          {/* Image column */}
          <div className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img
                  src={section.image}
                  alt={section.imageAlt}
                  className="w-full h-[360px] md:h-[440px] object-cover"
                  loading="lazy"
                />
              </div>
              <div className="absolute -top-4 -left-4 hidden md:flex items-center gap-2 bg-white rounded-2xl px-4 py-2.5 shadow-lg border border-gray-100">
                <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
                  <Icon className="w-4 h-4" style={{ color: RED }} />
                </div>
                <span className="text-sm font-bold text-gray-900">Step {section.step}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

const FAQS = [
  {
    q: "How much do I really need for a down payment as a first-time buyer?",
    a: "As little as 3% with a Conventional loan or 3.5% with FHA. Some VA and USDA loans require $0 down for eligible buyers. We'll match you with the program that fits your situation.",
  },
  {
    q: "Will checking my pre-approval hurt my credit?",
    a: "A pre-approval involves a credit pull, but the impact is minimal, typically a few points, and any inquiries for mortgages within a 45-day window count as a single inquiry.",
  },
  {
    q: "How long does the whole process take?",
    a: "Most CityWorth buyers close in 21 days from accepted offer. The full process, from pre-approval to keys, can take anywhere from a few weeks to a few months depending on your home search.",
  },
];

export default function FirstTimeHomebuyerGuidePage() {
  return (
    <div className="bg-white" data-testid="first-time-guide-page">
      <SEO
        title="First-Time Homebuyer Guide | Every step explained"
        description="A complete first-time homebuyer guide from CityWorth: budget, credit, pre-approval, home search, offer, inspection, appraisal, and closing. With real CTAs to talk to your team."
        path="/first-time-homebuyer-guide"
        jsonLd={buildGraph(
          webPageJsonLd({
            name: "First-Time Homebuyer Guide | CityWorth",
            description: "Step-by-step guide to buying your first home.",
            path: "/first-time-homebuyer-guide",
          }),
          faqJsonLd(FAQS),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Buy", path: "/buy-a-home" },
            { name: "First-Time Homebuyer Guide", path: "/first-time-homebuyer-guide" },
          ])
        )}
      />

      {/* Hero */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden bg-white">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-50 rounded-full blur-3xl opacity-50 -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 bg-red-50 rounded-full px-4 py-2 mb-6">
            <Heart className="w-4 h-4" style={{ color: RED }} />
            <span className="text-xs font-bold tracking-[0.18em] uppercase" style={{ color: RED }}>
              First-Time Homebuyer Guide
            </span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.02] tracking-tight mb-6">
            Your first home, <span style={{ color: RED }}>step by step.</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-10 max-w-2xl mx-auto">
            A clear, no-jargon walkthrough of the entire homebuying process, from figuring out your “why” to picking up the keys. Built for first-timers by the team that’s helped thousands of families become homeowners.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/get-started"
              className="inline-flex items-center justify-center bg-[#D41F30] hover:bg-[#B91C2A] text-white border-2 border-[#D41F30] hover:border-[#B91C2A] rounded-full px-6 py-3.5 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              data-testid="guide-hero-cta"
            >
              Start my homebuying plan
              <ArrowRight className="ml-2 w-4 h-4" />
            </a>
            <a
              href={TEAM_PHONE_TEL}
              className="inline-flex items-center justify-center border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full px-6 py-3.5 text-base font-semibold transition-all duration-300"
              data-testid="guide-hero-call"
            >
              <Phone className="mr-2 w-4 h-4" />
              {TEAM_PHONE}
            </a>
          </div>
        </div>
      </section>

      {/* Sections */}
      {SECTIONS.map((s, i) => (
        <SectionRow key={s.step} section={s} index={i} />
      ))}

      {/* Bonus tip + final CTA */}
      <section className="py-24 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D41F30] flex items-center justify-center mb-6">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-[1.05] tracking-tight">
            You don’t have to do this alone.
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            With CityWorth, you get real people guiding you every step of the way, and programs that help you save thousands on your home purchase.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/get-started"
              className="inline-flex items-center justify-center bg-[#D41F30] hover:bg-[#B91C2A] text-white border-2 border-[#D41F30] hover:border-[#B91C2A] rounded-full px-6 py-3.5 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              data-testid="guide-bottom-cta"
            >
              Start my homebuying plan
              <ArrowRight className="ml-2 w-4 h-4" />
            </a>
            <a
              href={TEAM_PHONE_TEL}
              className="inline-flex items-center justify-center bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full px-6 py-3.5 text-base font-semibold transition-all duration-300"
            >
              <Phone className="mr-2 w-4 h-4" />
              Talk to your team
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
